import { Paper, Stack } from "@mui/material";
import { StatusChip } from "features/shared/components/StatusChip";
import { useTranslation } from "react-i18next";
import { formatNumberBasedOnUser } from "utils/namesManipulation";
import { cn } from "utils/styling";
import { kwhPerM2Year, x } from "utils/textSymbols";

import { PerformanceRatioGauge } from "./PerformanceRatioGauge";
import { PerformanceRatioStatus } from "./performanceRatioUtils";

type PerformanceRatioCardProps = {
  buildingValue?: number;
  className?: string;
  ratio: number;
  referenceValue?: number;
  status: PerformanceRatioStatus;
  threshold: number;
  title: string;
  unit?: string;
};

export const PerformanceRatioCard = ({
  buildingValue,
  className,
  ratio,
  referenceValue,
  status,
  threshold,
  title,
  unit = kwhPerM2Year,
}: PerformanceRatioCardProps) => {
  const { t } = useTranslation();

  const isBad = status === "bad";

  return (
    <Paper className={cn("h-full p-4", className)}>
      <Stack className="h-full flex-col items-stretch gap-2">
        <Stack className="justify-between">
          <h3 className="text-lds-foreground-muted font-ds-body-regular-md">{title}</h3>
          {isBad && (
            <StatusChip className="normal-case" variant="error">
              {t("BuildingProfile.kpis.performanceRatioWorstPerformingBadge")}
            </StatusChip>
          )}
        </Stack>
        <Stack className="flex-col items-start gap-2">
          <p
            className={cn(
              "font-ds-number-semibold-xl",
              isBad ? "text-lds-purpose-status-negative" : "text-lds-purpose-status-positive"
            )}
          >
            {formatNumberBasedOnUser(ratio)}
            <span className="font-ds-number-regular-md"> {x}</span>
          </p>
          <span className="text-lds-foreground-muted font-ds-label-xs">
            {t("BuildingProfile.kpis.performanceRatioBuildingReference")}
          </span>
          <p className="text-lds-foreground-strong font-ds-number-semibold-md">
            {formatNumberBasedOnUser(buildingValue)} / {formatNumberBasedOnUser(referenceValue)}
            <span className="ml-1 font-ds-number-regular-sm"> {unit}</span>
          </p>
        </Stack>
        <PerformanceRatioGauge ratio={ratio} status={status} threshold={threshold} />
      </Stack>
    </Paper>
  );
};
